import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api } from '../api/client';
import { apiErrorText } from '../api/errorText';
import type { ErrorText } from '../api/errorText';
import type { RuntimeEnvironment, SetupState } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import EmiroLogo from '../components/EmiroLogo';
import { Stage, StagePanel } from '../components/Stage';

/// Music and photos are not among them: nothing files an album or a photograph yet, and a
/// library that catalogues nothing is a worse answer than not offering it.
const libraryKinds = ['Movies', 'Shows', 'Mixed', 'Other'] as const;

/// The first run. Four steps, and only the second one is mandatory: a server needs an
/// administrator, everything else can be done later from the admin UI.
export default function SetupWizardPage() {
  const { t, i18n } = useTranslation();
  const { setup, adopt, refresh } = useAuth();

  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ErrorText>();

  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');

  const [libraryName, setLibraryName] = useState('');
  const [libraryKind, setLibraryKind] = useState<string>('Movies');
  const [libraryPath, setLibraryPath] = useState('');
  const [libraryAdded, setLibraryAdded] = useState(false);

  /// The wizard's own view of the server, re-read when it reaches the hardware step. The
  /// shared state is left alone until the very end, so the wizard is not replaced by the
  /// application while it is still running.
  const [probe, setProbe] = useState<SetupState | undefined>(setup);

  /// Known only once the administrator exists, because asking needs an account. Everything the
  /// wizard says about paths afterwards is phrased for what it finds.
  const [runtime, setRuntime] = useState<RuntimeEnvironment>();

  useEffect(() => {
    if (step === 3) {
      void api.setupState().then(setProbe).catch(() => undefined);
    }
  }, [step]);

  const passwordsMatch = password.length > 0 && password === confirmation;

  const createAdministrator = async () => {
    setBusy(true);
    setError(undefined);

    try {
      const result = await api.createFirstAdministrator({
        name,
        password,
        preferredLanguage: i18n.language,
        clientName: 'Emiro Web',
      });

      adopt(result.token, result.user);
      void api.runtime().then(setRuntime).catch(() => undefined);
      setStep(2);
    } catch (caught) {
      setError(apiErrorText(caught, t));
    } finally {
      setBusy(false);
    }
  };

  const addLibrary = async () => {
    setBusy(true);
    setError(undefined);

    try {
      await api.createLibrary({
        id: crypto.randomUUID(),
        name: libraryName,
        kind: libraryKind,

        // Descriptions and artwork are fetched in this language. Whoever is setting the server
        // up is reading it in theirs, which is the best guess there is.
        metadataLanguage: i18n.language,
        locations: [
          { id: crypto.randomUUID(), providerId: 'tv.emiro.filesystem', address: libraryPath },
        ],
      });

      setLibraryAdded(true);
      setStep(3);
    } catch (caught) {
      // The provider validated the path and said what is wrong with it, in the user's
      // language: the code it returned is a key in that plugin's own catalogue.
      setError(apiErrorText(caught, t, runtime?.hostKind));
    } finally {
      setBusy(false);
    }
  };

  const steps = ['setup.step.welcome', 'setup.step.account', 'setup.step.library', 'setup.step.hardware'];

  return (
    <Stage>
      <StagePanel maxWidth={620}>
        <Stack spacing={3}>
          <Stack direction="row" spacing={2.5} sx={{ alignItems: 'center' }}>
            <EmiroLogo layout="mark" size={56} glow />
            <Box>
              <Typography variant="h2">{t('setup.title')}</Typography>
              <Typography variant="body2" color="text.secondary">
                {setup?.serverName} {setup?.serverVersion}
              </Typography>
            </Box>
          </Stack>

          <Stepper activeStep={step} alternativeLabel>
            {steps.map((key) => (
              <Step key={key}>
                <StepLabel>{t(key)}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Alert severity="error">
              <div>{error.message}</div>
              {error.remedy && (
                <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.85 }}>
                  {error.remedy}
                </Typography>
              )}
            </Alert>
          )}

          {step === 0 && (
            <Stack spacing={2}>
              <Typography variant="h3">{t('setup.welcome.title')}</Typography>
              <Typography color="text.secondary">{t('setup.welcome.body')}</Typography>
              <Button variant="contained" size="large" onClick={() => setStep(1)}>
                {t('common.next')}
              </Button>
            </Stack>
          )}

          {step === 1 && (
            <Stack spacing={2}>
              <Typography variant="h3">{t('setup.account.title')}</Typography>
              <Typography color="text.secondary">{t('setup.account.body')}</Typography>

              <TextField
                label={t('setup.account.name')}
                value={name}
                onChange={(event) => setName(event.target.value)}
                autoComplete="username"
                autoFocus
              />
              <TextField
                label={t('setup.account.password')}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                type="password"
                autoComplete="new-password"
                helperText={t('setup.account.hint')}
              />
              <TextField
                label={t('setup.account.confirm')}
                value={confirmation}
                onChange={(event) => setConfirmation(event.target.value)}
                type="password"
                autoComplete="new-password"
                error={confirmation.length > 0 && !passwordsMatch}
                helperText={confirmation.length > 0 && !passwordsMatch ? t('setup.account.mismatch') : ' '}
              />

              <Stack direction="row" spacing={1} sx={{ justifyContent: 'space-between' }}>
                <Button onClick={() => setStep(0)}>{t('common.back')}</Button>
                <Button
                  variant="contained"
                  disabled={busy || name.length < 2 || !passwordsMatch}
                  onClick={() => void createAdministrator()}
                >
                  {t('common.create')}
                </Button>
              </Stack>
            </Stack>
          )}

          {step === 2 && (
            <Stack spacing={2}>
              <Typography variant="h3">{t('setup.library.title')}</Typography>
              <Typography color="text.secondary">{t('setup.library.body')}</Typography>

              <TextField
                label={t('setup.library.name')}
                value={libraryName}
                onChange={(event) => setLibraryName(event.target.value)}
                autoFocus
              />
              <TextField
                select
                label={t('setup.library.kind')}
                value={libraryKind}
                onChange={(event) => setLibraryKind(event.target.value)}
                helperText={libraryKind === 'Mixed' ? t('setup.library.kindMixedHint') : ' '}
              >
                {libraryKinds.map((kind) => (
                  <MenuItem key={kind} value={kind}>
                    {t(`libraryKind.${kind}`)}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label={t('setup.library.path')}
                value={libraryPath}
                onChange={(event) => setLibraryPath(event.target.value)}
                helperText={t('setup.library.pathHint', { context: runtime?.hostKind })}
                placeholder="/media/movies"
              />

              <Stack direction="row" spacing={1} sx={{ justifyContent: 'space-between' }}>
                <Button onClick={() => setStep(3)}>{t('common.skip')}</Button>
                <Button
                  variant="contained"
                  disabled={busy || libraryName.length === 0 || libraryPath.length === 0}
                  onClick={() => void addLibrary()}
                >
                  {t('common.add')}
                </Button>
              </Stack>
            </Stack>
          )}

          {step === 3 && (
            <Stack spacing={2}>
              <Typography variant="h3">{t('setup.hardware.title')}</Typography>

              {probe?.hardwareError ? (
                <Alert severity="warning">
                  <AlertTitle>{t('dashboard.probeFailed')}</AlertTitle>
                  {probe.hardwareError}
                </Alert>
              ) : probe && probe.hardwareDeviceCount > 0 ? (
                <Alert severity="success" icon={<CheckCircleIcon />}>
                  {t('setup.hardware.found', { devices: probe.hardwareDeviceCount })}
                </Alert>
              ) : (
                <Alert severity="info">{t('setup.hardware.none')}</Alert>
              )}

              <Typography color="text.secondary">{t('setup.hardware.body')}</Typography>

              {libraryAdded && (
                <Alert severity="success" icon={<CheckCircleIcon />}>
                  {t('setup.library.added', { name: libraryName })}
                </Alert>
              )}

              <Button variant="contained" size="large" onClick={() => void refresh()}>
                {t('setup.done.enter')}
              </Button>
            </Stack>
          )}
        </Stack>
      </StagePanel>
    </Stage>
  );
}
